import React, { useState, useEffect } from "react";
import { SidebarOption, useStore } from "../../Store";
import { PageLayoutWithGetData } from "../Controls/PageLayout";
import Helpers from "../Functions/Helpers";
import { useNavigate } from "react-router-dom";
import { AxiosRequestConfig } from "axios";
import { Button } from "@chakra-ui/react";
import { UnAuthorized } from "../Result/UnAuthorized";
import { Flow } from "../../Types/SessionDetails";
import { useMessageParentOrRedirect } from "../../utils/useMessageParentOrRedirect";
import { useWipeData } from "../../utils/useWipeData";

export default function Start() {
  const navigate = useNavigate();

  // Not really sure why this is in the component, but I don't want to move it encase it breaks something
  let getSessionUrl =
    process.env.REACT_APP_PUBLIC_API_BASE_URI + "/portal/session/";

  const {
    setAccess,
    setToken,
    setSessionDetails,
    setLoading,
    data,
    setIsEmbedded,
    setSidebarDisplay,
  } = useStore();

  const { messageToParentUnauthorised } = useMessageParentOrRedirect();
  const wipeData = useWipeData();

  const [action, setAction] = useState(false);
  const [clearData, setClearData] = useState(false);
  const [showAuthorizedMessage, setAuthorizedMessage] = useState(false);

  // Grab a bunch of values from the query string. Maybe this should be in a useEffect.
  const queryString = window?.location?.search;
  const urlParams = new URLSearchParams(queryString);
  const sessionIdFromQueryString = urlParams.get("session-id");
  const consentIdFromQueryString = urlParams.get("consent-id");
  const customerIdFromQueryString = urlParams.get("customer-id");
  const flowFromQueryString = urlParams.get("flow");
  const isEmbeddedFromQueryString = urlParams.get("isEmbedded") === "true";
  const sideBarFromQueryString = urlParams.get(
    "sideBarOption"
  ) as SidebarOption;

  const sessionData = {
    sessionId: sessionIdFromQueryString as string,
    consentId: consentIdFromQueryString as string,
    customerId: customerIdFromQueryString as string,
    flow: flowFromQueryString as Flow,
  };

  // This useEffect checks that we have a sessionId in the query string.
  // If we have a query string, we clear all cached data.
  useEffect(() => {
    if (flowFromQueryString != null && sessionIdFromQueryString != null) {
      wipeData();
      setClearData(true);
      setLoading(true);
    } else {
      if (data?.session?.redirectUri != null) {
        let redirectUri = data?.session?.redirectUri;

        messageToParentUnauthorised({
          isEmbedded: isEmbeddedFromQueryString,
          redirectUri,
          consentId: sessionData.consentId,
          state: data?.session.state,
        });
      } else {
        setAuthorizedMessage(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Maybe this should be part of the previous useEffect.
  useEffect(() => {
    if (
      flowFromQueryString != null &&
      sessionIdFromQueryString != null &&
      clearData
    ) {
      setSessionDetails(sessionData);
      setAction(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearData]);

  // Maybe this should be part of the first useEffect.
  useEffect(() => {
    if (action) GetToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  // Calls the GetBearerTokenBySessionId ADRIS API endpoint.
  function GetToken() {
    setLoading(true);

    let config: AxiosRequestConfig = {
      method: "get",
      url: getSessionUrl + sessionData?.sessionId,
    };

    Helpers.CallApi(
      config,
      (response) => {
        // handle success
        if (response?.data && response?.data?.access_token) {
          const consentIdFromApi = response?.data?.consentId;
          // If the endpoint has returned a consentId then we need to use it. If we also got a consentId in the
          // query string then the values MUST match, else something strange has happened, and we shouldn't continue.
          // We have made the direct to bank flow not display the consent id in the redirect URI. This code will enable
          // us at anytime, to move to the same rule for the consent flow too. Changes will need to be made in the ADRIS
          // api to support it. When using the manage flow, the endpoint will not return a consentId.
          if (consentIdFromApi) {
            if (
              consentIdFromQueryString &&
              consentIdFromQueryString !== consentIdFromApi
            ) {
              setLoading(false);
              setAuthorizedMessage(true);
              return;
            }

            sessionData.consentId = consentIdFromApi;
          }

          setToken(response?.data);
          setAccess(true);
          setSessionDetails(sessionData);
          setIsEmbedded(isEmbeddedFromQueryString);
          setSidebarDisplay(sideBarFromQueryString);

          if (sessionData?.flow?.toLowerCase() === "consent")
            navigate("/ValueProposition");
          if (sessionData?.flow?.toLowerCase() === "manage")
            navigate("/DashboardLanding");
          if (sessionData?.flow?.toLowerCase() === "direct-to-bank" || sessionData?.flow?.toLowerCase() === "amend")
            navigate("/DhAuth");
        } else {
          setLoading(false);
          setAuthorizedMessage(true);
        }
      },
      () => {
        // handle error
        setLoading(false);
        setAuthorizedMessage(true);
      }
    );
  }

  return showAuthorizedMessage ? (
    <UnAuthorized
      extra={
        <Button
          colorScheme="blue"
          onClick={() => {
            wipeData();
            //Redirect back to the ADR site.
            window.history.back();
          }}
        >
          Go Back
        </Button>
      }
    />
  ) : (
    <PageLayoutWithGetData />
  );
}
